// The address in every country we hold it (UI-9): one row per country, each
// with its own consent and its own verdict. The same person is a contact row
// per Klaviyo account, and consent is per row — but an exclusion is on the
// address, globally or for one country, so the rows can disagree, and this
// table is where that shows.
//
// UI-10 adds whether the profile is still in that country's Klaviyo account:
// a row whose profile is gone there is not sent to.
//
// Absent field (an API older than UI-9): nothing at all, see `countryRows`.

import { Link } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import type { ContactDetail } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/emc/DataTable";
import { Country } from "@/components/emc/Flag";
import { StatusPill } from "@/components/emc/StatusPill";
import { countryRows } from "@/pages/contacts/model";
import type { CountryRow } from "@/pages/contacts/model";

const NONE = <span className="text-muted-foreground">—</span>;

const COLUMNS: ColumnDef<CountryRow>[] = [
  {
    id: "country",
    header: "Ország",
    cell: ({ row }) =>
      row.original.current ? (
        <span className="inline-flex items-center gap-2">
          <Country code={row.original.country} />
          <span className="text-muted-foreground">ez a sor</span>
        </span>
      ) : (
        <Link to={`/kontaktok/${row.original.contactId}`}>
          <Country code={row.original.country} />
        </Link>
      ),
  },
  { id: "consent", header: "Hozzájárulás", cell: ({ row }) => row.original.consent },
  {
    id: "sendable",
    header: "Küldhető",
    cell: ({ row }) => <StatusPill domain="sendable" state={row.original.sendable ? "yes" : "no"} />,
  },
  {
    id: "presence",
    header: "Klaviyo",
    cell: ({ row }) => <StatusPill domain="presence" state={row.original.presence} />,
  },
  {
    id: "reason",
    header: "Kizárás oka",
    cell: ({ row }) => row.original.exclusion?.reason ?? NONE,
  },
  {
    id: "scope",
    header: "Hatókör",
    cell: ({ row }) => row.original.exclusion?.scope ?? NONE,
  },
  {
    id: "source",
    header: "Honnan",
    cell: ({ row }) => row.original.exclusion?.source ?? NONE,
    meta: { muted: true },
  },
  {
    id: "since",
    header: "Mióta",
    cell: ({ row }) => row.original.exclusion?.since ?? NONE,
    meta: { mono: true, muted: true },
  },
  {
    // A hold's end (rule 1, 2026-10-16); an exclusion for good has none.
    id: "until",
    header: "Meddig",
    cell: ({ row }) => row.original.exclusion?.until ?? NONE,
    meta: { mono: true, muted: true },
  },
];

export function ContactCountries({ contact }: { contact: ContactDetail }) {
  const rows = countryRows(contact);
  if (rows === null || rows.length === 0) return null;
  return (
    <Card data-slot="contact-countries">
      <CardHeader>
        <CardTitle>Országonként</CardTitle>
        <CardDescription>
          A cím minden országban, ahol megvan nálunk. A hozzájárulás országonként külön; a globális
          kizárás minden országban érvényes, az országos csak abban az egyben. Ahol a profil már
          nincs meg az ország Klaviyo-fiókjában, oda nem küldünk.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          label="A cím országonként"
          variant="plain"
          className="-mx-5.5 border-t border-separator"
          columns={COLUMNS}
          data={rows}
          getRowId={(row) => String(row.contactId)}
        />
      </CardContent>
    </Card>
  );
}
